import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button, Breadcrumbs, Menu, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import Swal from "sweetalert2";
import "./RaiseComplaint.css";
import "./ProductTypeMasterManagement.css";
import { checklistTemplateAPI, checklistDocumentAPI, masterDataAPI, categoryMasterAPI } from "../services/api";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getDataTableExportRows } from "../utils/dataTableExportUtils";

const normalizeTemplate = (item) => ({
  templateCode: item.templateCode || item.TemplateCode || item.TEMPLATECODE || "",
  templateId: item.templateId || item.TemplateId || item.TEMPLATEID || "",
  templateName: item.templateName || item.TemplateName || item.TEMPLATENAME || "",
  templateDescription: item.templateDescription || item.TemplateDescription || item.TEMPLATEDESCRIPTION || "",
  marketCode: item.marketCode || item.MarketCode || item.MARKETCODE || "0000",
  marketName: item.marketName || item.MarketName || item.MARKETNAME || "",
  productTypeCode: item.productTypeCode || item.ProductTypeCode || item.PRODUCTTYPECODE || "0000",
  productTypeName: item.productTypeName || item.ProductTypeName || item.PRODUCTTYPENAME || "",
  catgCode: item.catgCode || item.CatgCode || item.CATGCODE || "0000",
  catgName: item.catgName || item.CatgName || item.CATGNAME || "",
  productQualityCode: item.productQualityCode || item.ProductQualityCode || item.ProductqualityCODE || "0000",
  productQualityName: item.productQualityName || item.ProductQualityName || item.PRODUCTQUALITYNAME || "",
  documentSLNo: item.documentSLNo || item.DocumentSLNo || item.DOCUMENTSLNO || null,
  bvCode: item.bvCode || item.BVCode || item.BVCODE || "",
  productCode: item.productCode || item.ProductCode || item.PRODUCTCODE || "",
  version: item.version || item.Version || item.VERSION || "",
  isActive: item.isActive ?? item.IsActive ?? false,
  displayOrder: item.displayOrder || item.DisplayOrder || item.DISPLAYORDER || 0,
  questionCount: item.questionCount || item.QuestionCount || 0,
  createdBy: item.createdBy || item.CreatedBy || item.CREATEDBY || "",
  createdOn: item.createdOn || item.CreatedOn || null,
  modifiedBy: item.modifiedBy || item.ModifiedBy || item.MODIFIEDBY || "",
  modifiedOn: item.modifiedOn || item.ModifiedOn || null,
});

const pickCategoryCode = (cat) =>
  String(
    cat?.catgCode ??
      cat?.CatgCode ??
      cat?.CATGCODE ??
      cat?.productCategoryId ??
      cat?.productcategoryId ??
      ""
  ).trim();

const pickCategoryLabel = (cat) =>
  String(
    cat?.catgName ||
      cat?.CatgName ||
      cat?.CATGNAME ||
      cat?.productCategoryDescription ||
      cat?.productcategoryDescription ||
      ""
  ).trim();

const resolveBusinessVerticalName = (catgCode, categoryList = []) => {
  const code = String(catgCode || "").trim();
  if (!code || !Array.isArray(categoryList) || categoryList.length === 0) return undefined;
  const match = categoryList.find((cat) => pickCategoryCode(cat) === code);
  const label = match ? pickCategoryLabel(match) : "";
  return label || undefined;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

/** DataTable column indexes: Market, Business Vertical, Template Name */
const CHECKLIST_TABLE_LEFT_HEADER_COLS = [1, 2, 3];

const applyChecklistTableLeftHeaders = (api) => {
  if (!api) return;
  CHECKLIST_TABLE_LEFT_HEADER_COLS.forEach((colIdx) => {
    const header = api.column(colIdx).header();
    if (!header) return;
    const $th = $(header);
    $th.addClass("text-start").css("text-align", "left");
    $th.find(".dt-column-header, .dt-column-title").css({
      textAlign: "left",
      justifyContent: "flex-start",
    });
  });
  const $wrapper = $(api.table().node()).closest(".dataTables_wrapper, .dt-container");
  $wrapper.find(".dt-scroll-head thead th").each(function () {
    const colIdx = parseInt($(this).attr("data-dt-column"), 10);
    if (!CHECKLIST_TABLE_LEFT_HEADER_COLS.includes(colIdx)) return;
    const $th = $(this);
    $th.addClass("text-start").css("text-align", "left");
    $th.find(".dt-column-header, .dt-column-title").css({
      textAlign: "left",
      justifyContent: "flex-start",
    });
  });
};

const ChecklistTemplateManagement = () => {
  console.log("ChecklistTemplateManagement component rendered");
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    marketCode: "",
    catgCode: "",
  });
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [copyingTemplateCode, setCopyingTemplateCode] = useState(null);
  const tableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const statusSelectRef = useRef(null);
  const marketSelectRef = useRef(null);
  const catgSelectRef = useRef(null);
  const isMountedRef = useRef(true);
  const catgSelect2InitializingRef = useRef(false);


  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    isMountedRef.current = true;
    loadMarkets();
    loadDocuments();
    fetchTemplates();
    
    // Use document-level event delegation for status toggle (more reliable)
    const handleDocumentClick = (e) => {
      const $target = $(e.target);
      const $statusToggle = $target.closest(".status-toggle");
      if ($statusToggle.length && $(tableRef.current).find($statusToggle).length) {
        e.preventDefault();
        e.stopPropagation();
        const code = $statusToggle.data("code");
        const currentActive =
          $statusToggle.data("active") === true ||
          $statusToggle.data("active") === "true" ||
          $statusToggle.data("active") === 1 ||
          $statusToggle.data("active") === "1";
        const newActive = !currentActive;
        console.log("Status toggle clicked (document):", { code, currentActive, newActive });
        if (code) {
          handleToggleStatus(code, newActive);
        }
      }
    };
    
    $(document).on("click", ".status-toggle", handleDocumentClick);
    
    return () => {
      isMountedRef.current = false;
      $(document).off("click", ".status-toggle", handleDocumentClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCategories = async (marketCode) => {
    if (!marketCode) {
      setCategories([]);
      return;
    }

    try {
      setCategories([]);
      const response = await categoryMasterAPI.getPaged({
        marketCode,
        isActive: true,
        pageNumber: 1,
        pageSize: 5000,
      });

      const categoriesData =
        response?.data?.Categories ||
        response?.data?.categories ||
        response?.data?.data?.Categories ||
        response?.data?.data?.categories ||
        (Array.isArray(response?.data) ? response.data : []);

      setCategories(Array.isArray(categoriesData) ? [...categoriesData] : []);
    } catch (e) {
      console.error("Error loading business verticals for market", marketCode, ":", e);
      setCategories([]);
    }
  };

  const loadMarkets = async () => {
    try {
      const response = await masterDataAPI.getMarkets();
      if (response?.success && response.data) {
        const marketList = response.data.markets || response.data.Markets || [];
        setMarkets(marketList);
      }
    } catch (e) {
      console.error("Error loading markets:", e);
    }
  };


  const loadDocuments = async () => {
    try {
      const response = await checklistDocumentAPI.getAll({
        status: "active",
        pageNumber: 1,
        pageSize: 100,
      });
      if (response?.success && response.data?.data) {
        setDocuments(response.data.data);
      }
    } catch (e) {
      console.error("Error loading documents:", e);
    }
  };

  const fetchTemplates = async (activeFilters = filters, categoryList = categories) => {
    try {
      setLoading(true);
      const marketCode = String(activeFilters.marketCode || "").trim();
      const catgCode = String(activeFilters.catgCode || "").trim();
      const businessVertical = resolveBusinessVerticalName(catgCode, categoryList);

      const response = await checklistTemplateAPI.getAll({
        searchText: activeFilters.search || undefined,
        status: activeFilters.status,
        marketCode: marketCode || undefined,
        catgCode: catgCode || undefined,
        businessVertical,
        pageNumber,
        pageSize,
      });

      if (response?.success && response.data?.data) {
        const normalized = response.data.data.map(normalizeTemplate);
        // Sort by createdOn in descending order (newest first)
        const sorted = normalized.sort((a, b) => {
          const dateA = a.createdOn ? new Date(a.createdOn).getTime() : 0;
          const dateB = b.createdOn ? new Date(b.createdOn).getTime() : 0;
          return dateB - dateA; // Descending order
        });
        setTemplates(sorted);
        setTotalRecords(response.data.totalRecords || 0);
        initializeDataTable(sorted);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load checklist templates",
      });
    } finally {
      setLoading(false);
    }
  };

  const initializeDataTable = (data) => {
    if (dataTableInstanceRef.current) {
      try {
        const $table = $(tableRef.current);
        $table.off("click", ".manage-questions-btn");
        $table.off("click", ".view-questions-btn");
        $table.off("click", ".copy-template");
        $table.off("click", ".delete-template");
        $table.off("click", ".status-toggle");
        if ($.fn.DataTable && $.fn.DataTable.isDataTable(tableRef.current)) {
          dataTableInstanceRef.current.destroy();
        }
      } catch (e) {
        console.warn("Error destroying DataTable:", e);
      }
      dataTableInstanceRef.current = null;
    }

    if (tableRef.current) {
      try {
        if (!$.fn.DataTable) {
          DataTable($);
        }

        dataTableInstanceRef.current = $(tableRef.current).DataTable({
          data: data,
          paging: true,
          searching: true,
          info: true,
          lengthChange: true,
          lengthMenu: [5, 10, 25, 50, 100],
          dom: "lfrtip",
          ordering: true,
          destroy: true,
          scrollX: true,
          autoWidth: false,
          columns: [
            {
              title: "Template Code",
              data: "templateCode",
              render: (d) => d || "-",
              visible: false,
            },
            {
              title: "Market",
              data: null,
              className: "text-start",
              render: (d, type, row) => row.marketName || row.MarketName || row.marketCode || "-",
            },
            {
              title: "Business Vertical",
              data: null,
              className: "text-start",
              render: (d, type, row) => row.catgName || row.CatgName || row.catgCode || "-",
            },
            {
              title: "Template Name",
              data: "templateName",
              className: "text-start",
              render: (d) => d || "-",
            },
            {
              title: "Document",
              data: "documentSLNo",
              className: "text-center",
              render: (data, type, row) => {
                const doc = documents.find((d) => (d.sLNo || d.SLNo) === data);
                return doc ? doc.fileName || doc.FileName : "-";
              },
              visible: false,
            },
            {
              title: "Questions",
              data: "questionCount",
              className: "text-center",
              render: (data) => data || 0,
            },
            {
              title: "Created On",
              data: "createdOn",
              className: "text-center",
              render: (data) => formatDateTime(data),
            },
            {
              title: "Modified On",
              data: "modifiedOn",
              className: "text-center",
              render: (data) => formatDateTime(data),
            },
            {
              title: "Status",
              data: null,
              orderable: false,
              render: (d, type, row) => {
                const templateCode = d?.templateCode || row?.templateCode || "";
                const isActive = d?.isActive === true || d?.isActive === 1 || row?.isActive === true || row?.isActive === 1;
                const toggleClass = isActive ? "active" : "inactive";
                const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
                return `<div class="d-flex justify-content-center">
                  <button class="btn btn-sm status-toggle ${toggleClass}" 
                    data-code="${templateCode}" 
                    data-active="${isActive}"
                    type="button"
                    title="${
                      isActive ? "Click to deactivate" : "Click to activate"
                    }"
                    style="background: transparent !important; 
                           border: none !important; 
                           padding: 0 !important;
                           font-size: 24px !important;
                           color: ${isActive ? "#4caf50" : "#9e9e9e"} !important;
                           cursor: pointer !important;
                           pointer-events: auto !important;
                           z-index: 10 !important;
                           position: relative !important;
                           transition: all 0.3s ease;">
                    <i class="bi ${toggleIcon}" style="pointer-events: none;"></i>
                  </button>
                </div>`;
              },
            },
            {
              title: "Action",
              data: null,
              orderable: false,
              searchable: false,
              render: (d) =>
                `<div class="d-flex justify-content-center gap-2">
                  <button class="btn btn-sm edit-template-btn" data-code="${d.templateCode}" title="Edit Questions"
                    style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-pencil-fill"></i>
                  </button>
                  <button class="btn btn-sm view-questions-btn" data-code="${d.templateCode}" title="Edit Template"
                    style="background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-pencil-square"></i>
                  </button>
                  <button class="btn btn-sm copy-template" data-code="${d.templateCode}" title="Copy Template"
                    style="background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-files"></i>
                  </button>
                  <button class="btn btn-sm delete-template" data-code="${d.templateCode}" title="Delete Template"
                    style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-trash-fill"></i>
                  </button>
                </div>`,
            },
          ],
          order: [[6, "desc"]], // Order by Created On descending
          language: {
            emptyTable: "No checklist templates found",
          },
          initComplete: function () {
            applyChecklistTableLeftHeaders(this.api());
            try {
              const wrapper = $(tableRef.current).closest(
                ".dataTables_wrapper, .dt-container"
              );
              if (wrapper.length === 0) return;

              const lengthEl = wrapper.find(
                ".dt-length, .dataTables_length"
              );
              const searchEl = wrapper.find(
                ".dt-search, .dataTables_filter"
              );

              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $(
                    "<div class='dtFltrTop d-flex flex-wrap align-items-center justify-content-between w-100'></div>"
                  );
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              const infoEl = wrapper.find(".dt-info, .dataTables_info");
              const pagingEl = wrapper.find(
                ".dt-paging, .dataTables_paginate"
              );
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap align-items-center justify-content-between w-100'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            } catch (err) {
              console.warn("DataTable layout wrap failed:", err);
            }
          },
          headerCallback: function () {
            applyChecklistTableLeftHeaders(this.api());
          },
          drawCallback: function () {
            applyChecklistTableLeftHeaders(this.api());
            // Re-attach event handlers after each draw
            const $table = $(tableRef.current);
            $table
              .off("click", ".status-toggle")
              .on("click", ".status-toggle", function (e) {
                e.preventDefault();
                e.stopPropagation();
                const $btn = $(this);
                const code = $btn.data("code");
                const currentActive =
                  $btn.data("active") === true ||
                  $btn.data("active") === "true" ||
                  $btn.data("active") === 1 ||
                  $btn.data("active") === "1";
                const newActive = !currentActive;
                console.log("Status toggle clicked (drawCallback):", { code, currentActive, newActive });
                if (code) {
                  handleToggleStatus(code, newActive);
                }
              });
          },
        });

        // Attach event handlers
        const $table = $(tableRef.current);
        $table
          .off("click", ".edit-template-btn")
          .on("click", ".edit-template-btn", function () {
            const code = $(this).data("code");
            handleEdit(code);
          });

        $table
          .off("click", ".view-questions-btn")
          .on("click", ".view-questions-btn", function () {
            const code = $(this).data("code");
            navigate(`/checklist-templates/edit/${code}`);
          });

        $table
          .off("click", ".copy-template")
          .on("click", ".copy-template", function () {
            const code = $(this).data("code");
            handleCopy(code);
          });

        $table
          .off("click", ".delete-template")
          .on("click", ".delete-template", function () {
            const code = $(this).data("code");
            handleDelete(code);
          });

      // Attach status toggle handler - must be after DataTable initialization
      const $statusTable = $(tableRef.current);
      $statusTable
        .off("click", ".status-toggle")
        .on("click", ".status-toggle", function (e) {
          e.preventDefault();
          e.stopPropagation();
          const $btn = $(this);
          const code = $btn.data("code");
          const currentActive =
            $btn.data("active") === true ||
            $btn.data("active") === "true" ||
            $btn.data("active") === 1 ||
            $btn.data("active") === "1";
          const newActive = !currentActive;
          console.log("Status toggle clicked:", { code, currentActive, newActive, button: $btn[0] });
          if (code) {
            handleToggleStatus(code, newActive);
          } else {
            console.error("No template code found for status toggle");
          }
        });
      } catch (error) {
        console.error("Error initializing DataTable:", error);
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    fetchTemplates(
      { ...filters, search: String(filters.search || "").trim() },
      categories
    );
  };

  const handleReset = () => {
    setFilters({
      search: "",
      status: "all",
      marketCode: "",
      catgCode: "",
    });
    setCategories([]);
    // Reset Select2 dropdowns
    if (marketSelectRef.current) {
      const $marketSelect = $(marketSelectRef.current);
      if ($marketSelect.data("select2")) {
        $marketSelect.val("").trigger("change");
      }
    }
    if (catgSelectRef.current) {
      const $catgSelect = $(catgSelectRef.current);
      if ($catgSelect.data("select2")) {
        $catgSelect.val("").trigger("change");
      }
    }
    setTimeout(() => {
      fetchTemplates();
    }, 100);
  };

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      marketCode: "",
      catgCode: "",
    });
    setCategories([]);
    // Reset Select2 dropdowns
    if (marketSelectRef.current) {
      const $marketSelect = $(marketSelectRef.current);
      if ($marketSelect.data("select2")) {
        $marketSelect.val("").trigger("change");
      }
    }
    if (catgSelectRef.current) {
      const $catgSelect = $(catgSelectRef.current);
      if ($catgSelect.data("select2")) {
        $catgSelect.val("").trigger("change");
      }
    }
    setTimeout(() => {
      fetchTemplates();
    }, 100);
  };

  // Export to PDF
  const exportToPDF = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, templates);
    try {
      if (!exportRows || exportRows.length === 0) {
        Swal.fire("Info", "No data to export", "info");
        return;
      }

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text("Checklist Configuration Data", 14, 15);

      const tableData = exportRows.map((template, index) => [
        String(index + 1),
        String(template.templateName || "-"),
        String(template.marketName || template.marketCode || "-"),
        String(template.catgName || template.catgCode || "-"),
        String(formatDateTime(template.createdOn) || "-"),
        String(formatDateTime(template.modifiedOn) || "-"),
        template.isActive ? "Active" : "Inactive",
      ]);

      autoTable(doc, {
        head: [["SNo.", "Template Name", "Market", "Business Vertical", "Created On", "Modified On", "Status"]],
        body: tableData,
        startY: 25,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [102, 126, 234] },
        margin: { top: 25 },
      });

      doc.save(`Checklist_Template_Master_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      Swal.fire({
        title: "Error",
        text: `Failed to export PDF: ${error.message || "Unknown error"}`,
        icon: "error",
      });
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, templates);
    try {
      const headers = ["SNo.", "Template Name", "Market", "Business Vertical", "Created On", "Modified On", "Status"];
      const csvData = exportRows.map((template, index) => [
        index + 1,
        template.templateName || "",
        template.marketName || template.marketCode || "",
        template.catgName || template.catgCode || "",
        formatDateTime(template.createdOn),
        formatDateTime(template.modifiedOn),
        template.isActive ? "Active" : "Inactive",
      ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Checklist_Template_Master_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      Swal.fire("Error", "Failed to export CSV", "error");
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, templates);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Checklist Configuration");

      worksheet.mergeCells("A1:G1");
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value = "Checklist Configuration Data";
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      const headerRow = worksheet.getRow(3);
      headerRow.values = ["SNo.", "Template Name", "Market", "Business Vertical", "Created On", "Modified On", "Status"];
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF667EEA" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      exportRows.forEach((template, index) => {
        const row = worksheet.addRow([
          index + 1,
          template.templateName || "",
          template.marketName || template.marketCode || "",
          template.catgName || template.catgCode || "",
          formatDateTime(template.createdOn),
          formatDateTime(template.modifiedOn),
          template.isActive ? "Active" : "Inactive",
        ]);
        row.alignment = { vertical: "middle" };
      });

      worksheet.columns.forEach((column) => {
        column.width = 20;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Checklist_Template_Master_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire("Error", "Failed to export Excel", "error");
    }
  };

  // Handle export menu
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportOption = (format) => {
    handleExportMenuClose();
    if (format === "excel") {
      exportToExcel();
    } else if (format === "csv") {
      exportToCSV();
    } else if (format === "pdf") {
      exportToPDF();
    }
  };

  const handleEdit = (templateCode) => {
    // Navigate to checklist questions tree page for managing questions
    navigate(`/checklist-questions/tree?templateCode=${templateCode}`);
  };

  const handleToggleStatus = async (templateCode, newStatus) => {
    const actionText = newStatus ? "activate" : "deactivate";
    const result = await Swal.fire({
      title: "Confirm Status Change",
      text: `Are you sure you want to ${actionText} this checklist template?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: newStatus ? "#4caf50" : "#ff9800",
      cancelButtonColor: "#6c757d",
      confirmButtonText: newStatus ? "Yes, activate!" : "Yes, deactivate!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const template = templates.find((t) => t.templateCode === templateCode);
        if (!template) {
          Swal.fire("Error", "Template not found", "error");
          return;
        }

        const modifiedBy = currentUser?.userIdCode || currentUser?.userName || "SYSADMIN001";
        const response = await checklistTemplateAPI.updateStatus(templateCode, newStatus, modifiedBy);
        
        if (response && response.success) {
          Swal.fire(
            "Success!",
            response.message || `Checklist template has been ${actionText}d.`,
            "success"
          );
          fetchTemplates();
        } else {
          Swal.fire(
            "Error",
            response?.message || `Failed to ${actionText} checklist template`,
            "error"
          );
        }
      } catch (error) {
        console.error(`Error ${actionText}ing checklist template:`, error);
        Swal.fire(
          "Error",
          error?.response?.data?.message ||
            `Failed to ${actionText} checklist template. Please try again.`,
          "error"
        );
      }
    }
  };

  const handleCopy = async (templateCode) => {
    if (copyingTemplateCode) return;

    const result = await Swal.fire({
      title: "Copy checklist template?",
      text: "Are you sure you want to copy this checklist template?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#43e97b",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, copy it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      setCopyingTemplateCode(templateCode);
      $(tableRef.current).find(".copy-template").prop("disabled", true);

      const createdBy = currentUser?.userIdCode || currentUser?.userName || "SYSADMIN001";
      const response = await checklistTemplateAPI.copy(templateCode, createdBy);

      if (response?.success) {
        const newCode =
          response?.data?.templateCode ||
          response?.data?.TemplateCode ||
          response?.data?.id ||
          response?.data?.Id ||
          "";

        await fetchTemplates();

        if (newCode) {
          navigate(
            `/checklist-questions/tree?templateCode=${encodeURIComponent(newCode)}`,
            {
              state: {
                isCopyDraft: true,
                copyFromTemplateCode: templateCode,
              },
            }
          );
        } else {
          await Swal.fire({
            icon: "success",
            title: "Success",
            text: response?.message || "Checklist template copied successfully",
            timer: 2000,
            showConfirmButton: false,
          });
        }
      } else {
        Swal.fire("Error", response?.message || "Failed to copy checklist template", "error");
      }
    } catch (error) {
      console.error("Error copying checklist template:", error);
      const errorMessage =
        error?.response?.data?.message || error?.message || "Failed to copy checklist template";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setCopyingTemplateCode(null);
      $(tableRef.current).find(".copy-template").prop("disabled", false);
    }
  };

  const handleDelete = async (templateCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the checklist template and all related questions. This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || currentUser?.userName || "SYSADMIN001";
        const response = await checklistTemplateAPI.delete(templateCode, modifiedBy);
        if (response?.success) {
          Swal.fire("Deleted!", "The template and related questions have been deleted.", "success");
          fetchTemplates();
        } else {
          Swal.fire("Error", response?.message || "Failed to delete template", "error");
        }
      } catch (error) {
        console.error("Error deleting template:", error);
        const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete template";
        Swal.fire("Error", errorMessage, "error");
      }
    }
  };


  // Cleanup Select2 before React removes nodes
  useLayoutEffect(() => {
    return () => {
      try {
        if (statusSelectRef.current) {
          const $status = $(statusSelectRef.current);
          if ($status.length && $status.data("select2")) {
            try {
              $status.off("change select2:select select2:change");
              // Check if element is still in DOM before destroying
              if ($status.parent().length) {
                $status.select2("destroy");
              }
            } catch (e) {
              // Select2 might already be destroyed or node removed
            }
          }
        }
        if (marketSelectRef.current) {
          const $market = $(marketSelectRef.current);
          if ($market.length && $market.data("select2")) {
            try {
              $market.off("select2:select select2:change");
              // Check if element is still in DOM before destroying
              if ($market.parent().length) {
                $market.select2("destroy");
              }
            } catch (e) {
              // Select2 might already be destroyed or node removed
            }
          }
        }
        if (catgSelectRef.current) {
          const $catg = $(catgSelectRef.current);
          if ($catg.length && $catg.data("select2")) {
            try {
              $catg.off("select2:select select2:change");
              // Check if element is still in DOM before destroying
              if ($catg.parent().length) {
                $catg.select2("destroy");
              }
            } catch (e) {
              // Select2 might already be destroyed or node removed
            }
          }
        }
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, []); // Unmount-only: omitting [] ran this cleanup every render and destroyed Select2

  // Initialize Select2 dropdowns
  useEffect(() => {
    if (statusSelectRef.current) {
      const $select = $(statusSelectRef.current);
      // Safely destroy existing Select2 instance
      try {
        if ($select.data("select2")) {
          $select.off("select2:select select2:change");
          $select.select2("destroy");
        }
      } catch (e) {
        console.warn("Error destroying Select2:", e);
      }
      
      // Check if Select2 is available
      if (typeof $.fn.select2 === 'undefined') {
        console.error("Select2 is not available for status dropdown");
        return;
      }
      
      try {
        // Verify the element is in the DOM
        if (!$select.parent().length) {
          console.warn("Status select element not in DOM yet");
          return;
        }
        
        $select.select2({
          width: "100%",
          minimumResultsForSearch: Infinity,
        });
        $select.on("select2:select select2:change", function (e) {
          const selectedStatus = $select.val() || "";
          handleFilterChange("status", selectedStatus);
        });
      } catch (error) {
        console.error("Error initializing Status Select2:", error);
      }
    }
  }, []);

  useEffect(() => {
    if (!catgSelectRef.current) return;
    
    // Prevent multiple simultaneous initializations
    if (catgSelect2InitializingRef.current) return;
    
    const timer = setTimeout(() => {
      if (!isMountedRef.current || !catgSelectRef.current) return;
      
      catgSelect2InitializingRef.current = true;
      
      const $select = $(catgSelectRef.current);
      if (!$select.length) {
        catgSelect2InitializingRef.current = false;
        return;
      }
      
      // Check if Select2 is available
      if (typeof $.fn.select2 === 'undefined' || typeof $.fn.select2 !== 'function') {
        console.error("Select2 is not available for category dropdown");
        catgSelect2InitializingRef.current = false;
        return;
      }
      
      // Verify the element is in the DOM
      if (!$select.parent().length || !$select.is(':visible')) {
        catgSelect2InitializingRef.current = false;
        return;
      }
      
      const isSelect2Initialized = $select.data("select2");
      
      // If Select2 is already initialized, just update value and disabled state
      // Don't manipulate DOM - let React handle option rendering
      if (isSelect2Initialized) {
        // Update disabled state
        const isDisabled = !filters.marketCode || filters.marketCode === "";
        $select.prop("disabled", isDisabled);
        
        // Trigger Select2 to refresh (it will read the new options from React-rendered DOM)
        $select.trigger("change.select2");
        
          // Restore or set value
          if (filters.catgCode && !isDisabled && categories.length > 0) {
            const categoryExists = categories.some(
              (cat) => pickCategoryCode(cat) === filters.catgCode
            );
          if (categoryExists) {
            $select.val(filters.catgCode).trigger("change.select2");
          } else {
            $select.val("").trigger("change.select2");
            if (isMountedRef.current) {
              handleFilterChange("catgCode", "");
            }
          }
        } else {
          $select.val("").trigger("change.select2");
        }
        
        catgSelect2InitializingRef.current = false;
        return;
      }
      
      // Initialize Select2 for the first time
      try {
        // Enable/disable based on market selection
        const isDisabled = !filters.marketCode || filters.marketCode === "";
        $select.prop("disabled", isDisabled);
        
        // Initialize Select2 (it will read options from React-rendered DOM)
        $select.select2({
          width: "100%",
          placeholder: isDisabled ? "Please select Market first" : "Select Business Vertical",
          allowClear: false,
        });
        
        // Bind event handler
        $select.on("select2:select select2:change", function (e) {
          if (isMountedRef.current) {
            const selectedCatgCode = $select.val() || "";
            handleFilterChange("catgCode", selectedCatgCode);
          }
        });
        
        // Set initial value if there's a selected category
        if (filters.catgCode && !isDisabled && categories.length > 0) {
          const categoryExists = categories.some(
            (cat) => pickCategoryCode(cat) === filters.catgCode
          );
          if (categoryExists) {
            $select.val(filters.catgCode).trigger("change.select2");
          }
        }
        
        catgSelect2InitializingRef.current = false;
      } catch (error) {
        console.error("Error initializing Select2:", error);
        catgSelect2InitializingRef.current = false;
      }
    }, 100);
    
    return () => {
      clearTimeout(timer);
      catgSelect2InitializingRef.current = false;
    };
  }, [categories, filters.marketCode, filters.catgCode]);

  useEffect(() => {
    if (marketSelectRef.current && markets.length > 0) {
      const $select = $(marketSelectRef.current);
      if (!$select.length) return;
      
      // Check if Select2 is already initialized
      const isSelect2Initialized = $select.data("select2");
      
      if (isSelect2Initialized) {
        // Select2 is already initialized, just update value if needed
        // Don't re-bind handlers as they're already bound and working
        if (filters.marketCode && $select.val() !== filters.marketCode) {
          $select.val(filters.marketCode).trigger("change.select2");
        }
        return; // Don't reinitialize or re-bind handlers
      }
      
      // Use a timeout to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        if (!isMountedRef.current || !marketSelectRef.current) return;
        
        const $selectEl = $(marketSelectRef.current);
        if (!$selectEl.length || $selectEl.data("select2")) return;
        
        // Check if Select2 is available and fully loaded
        if (typeof $.fn.select2 === 'undefined' || typeof $.fn.select2 !== 'function') {
          console.error("Select2 is not available");
          return;
        }
        
        // Verify the element is in the DOM and visible
        if (!$selectEl.parent().length || !$selectEl.is(':visible')) {
          console.warn("Select element not in DOM or not visible yet");
          return;
        }
        
        try {
          // Use requestAnimationFrame to ensure DOM is fully ready
          requestAnimationFrame(() => {
            if (!isMountedRef.current || !marketSelectRef.current) return;
            
            const $el = $(marketSelectRef.current);
            if (!$el.length || $el.data("select2")) return;
            
            try {
              $el.select2({
                width: "100%",
                placeholder: "Select Market",
                allowClear: false,
              });
              
              console.log("Market Select2 initialized");
              
              // Use Select2's specific events
              $el.on("select2:select select2:change", function (e) {
                if (!isMountedRef.current) return;
                const selectedMarketCode = $el.val() || "";
                console.log("Market dropdown changed to:", selectedMarketCode);
                
                // Always update the market code filter
                handleFilterChange("marketCode", selectedMarketCode);
                // Clear category selection when market changes
                handleFilterChange("catgCode", "");
                
                // Load categories for the selected market
                if (selectedMarketCode) {
                  console.log("Calling loadCategories with:", selectedMarketCode);
                  loadCategories(selectedMarketCode);
                } else {
                  console.log("No market selected, clearing categories");
                  setCategories([]);
                }
              });
              
              // Set initial value if filters.marketCode exists
              if (filters.marketCode) {
                $el.val(filters.marketCode).trigger("change.select2");
              }
            } catch (error) {
              console.error("Error initializing Market Select2 in requestAnimationFrame:", error);
            }
          });
        } catch (error) {
          console.error("Error setting up Market Select2 initialization:", error);
        }
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [markets, filters.marketCode]);

  // Load categories when marketCode changes (backup/initial load)
  useEffect(() => {
    if (filters.marketCode) {
      console.log("Market code changed in filters, loading categories:", filters.marketCode);
      loadCategories(filters.marketCode);
    } else {
      setCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.marketCode]);



  useEffect(() => {
    fetchTemplates(filters, categories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, categories]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            className="add-edit-product-breadcrumb"
          >
            <Typography
              color="text.primary"
              className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
            >
              Checklist Configuration
            </Typography>
          </Breadcrumbs>
          <Typography variant="body2" sx={{ color: "#666666", mt: 1 }}>
            Manage checklist configuration.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Export Button with Dropdown */}
          <Button
            onClick={handleExportMenuOpen}
            sx={{
              background: "transparent",
              border: "1px solid #667eea",
              color: "#667eea",
              fontWeight: 500,
              padding: "8px 10px",
              borderRadius: "6px",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "36px",
              height: "36px",
              "&:hover": {
                background: "transparent",
                borderColor: "#764ba2",
                color: "#764ba2",
              },
              "& .MuiSvgIcon-root": {
                color: "#667eea",
              },
              "&:hover .MuiSvgIcon-root": {
                color: "#764ba2",
              },
            }}
            title="Export"
          >
            <FileDownloadIcon sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }} />
            <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={() => handleExportOption("excel")}>
              Excel
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("csv")}>
              CSV
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("pdf")}>
              PDF
            </MenuItem>
          </Menu>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/checklist-templates/add")}
            sx={{
              background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
              border: "none",
              color: "white",
              fontWeight: 600,
              fontSize: "11px",
              boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
              borderRadius: "4px",
              textTransform: "uppercase",
              padding: "8px 24px",
              "&:hover": {
                background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                boxShadow: "0 6px 20px rgba(245, 87, 108, 0.6)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Add Template
          </Button>
        </Box>
      </Box>

      {/* Filter Panel */}
      <div className="card-panel mb-4 filter-panel" style={{ marginBottom: "24px" }}>
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          style={{ marginBottom: "14px" }}
        >
          <SearchIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter: Market, Business Vertical</span>
        </div>

        <div className="container-fluid">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6 col-lg-3 srchDvwrpr d-none" aria-hidden="true">
              <label className="input-label d-block mb-1 text-start">
                Search
              </label>
              <div className="input-icon" style={{ position: "relative" }}>
                <input
                  type="text"
                  className="form-control form-control-light text-start"
                  placeholder="Search by Code / Name"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleFilterChange("search", filters.search.trim());
                      fetchTemplates();
                    }
                  }}
                  style={{ height: "42px", textAlign: "left" }}
                />
                <SearchIcon
                  className="icon-right"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    width: "16px",
                    height: "16px",
                    color: "#6c757d",
                  }}
                />
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Market
              </label>
              <select
                key={`market-${markets.length}`}
                ref={marketSelectRef}
                className="form-select form-control-light filter-select text-start"
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="">Select Market</option>
                {markets && markets.length > 0 ? (
                  markets.map((market) => {
                    const code = market.marketCode || market.MarketCode || "";
                    const name = market.marketName || market.MarketName || "";
                    return (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    );
                  })
                ) : (
                  <option value="" disabled>Loading markets...</option>
                )}
              </select>
            </div>

            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Business Vertical
              </label>
              <select
                ref={catgSelectRef}
                className="form-select form-control-light filter-select text-start"
                style={{ height: "42px", textAlign: "left" }}
                disabled={!filters.marketCode || filters.marketCode === ""}
              >
                <option value="">
                  {!filters.marketCode || filters.marketCode === ""
                    ? "Please select Market first"
                    : categories.length === 0
                      ? "Loading categories..."
                      : "Select Business Vertical"}
                </option>
                {categories && categories.length > 0 ? (
                  categories.map((cat) => {
                    const code = pickCategoryCode(cat);
                    const name = pickCategoryLabel(cat);
                    return (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    );
                  })
                ) : null}
              </select>
            </div>

{/*             <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Status
              </label>
              <select
                ref={statusSelectRef}
                className="form-select form-control-light filter-select text-start"
                value={filters.status}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div> */}

            <div className="col-12 col-md-6 col-lg-3">
              <label
                className="input-label d-block mb-1 text-start"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons justify-content-start">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={() => {
                    handleFilterChange("search", filters.search.trim());
                    fetchTemplates(
                      { ...filters, search: String(filters.search || "").trim() },
                      categories
                    );
                  }}
                  style={{
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <SearchIcon fontSize="small" className="me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={resetFilters}
                  title="Reset"
                  style={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(132, 250, 176, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(132, 250, 176, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
          mt: 2,
        }}
      >
        <div className="card-panel mt-0">
          {loading && templates.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography>Loading templates...</Typography>
            </Box>
          ) : templates.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No templates found. Click "Add Template" to create a new template.
              </Typography>
            </Box>
          ) : (
            <div className="table-responsive">
              <table
                ref={tableRef}
                className="table table-striped table-bordered complaint-table custmTblLst"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </Paper>

    </Box>
  );
};

export default ChecklistTemplateManagement;
